import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  cancelShift,
  formatTimeOfDay,
  upsertShift,
} from '@/lib/scheduling';
import type { ConflictCode, Shift, Staff, TimeFormat } from '@/lib/types';

export interface ShiftEditorModalProps {
  open: boolean;
  date: string;
  half: 'am' | 'pm' | null;
  shift: Shift | null;
  locationId: string;
  staff: Staff[];
  defaultStartTime: string;
  defaultEndTime: string;
  timeFormat: TimeFormat;
  prefillStaffId?: string | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

export function ShiftEditorModal(props: ShiftEditorModalProps) {
  const {
    open,
    date,
    shift,
    locationId,
    staff,
    defaultStartTime,
    defaultEndTime,
    timeFormat,
    prefillStaffId,
    onClose,
    onSaved,
  } = props;

  const isEdit = !!shift;
  // Migration 0096: dropdown now shows ALL staff, sorted with active first
  // and pending/inactive entries annotated for the shift date. Compared to
  // the previous "filter out anyone with active_to in the past" rule, this
  // also catches future-dated active_to (someone whose last day is past
  // the shift date) and future-dated active_from (not yet started).
  // The conflict-override flow on the server still permits saving with a
  // reason -- the annotation just makes the date issue visible BEFORE the
  // user picks the staff member.
  const staffOptions = useMemo(() => {
    const list = staff.map((s) => {
      let status: 'active' | 'pending' | 'inactive' = 'active';
      let note = '';
      if (s.active_to && s.active_to < date) {
        status = 'inactive';
        note = ` · inactive since ${s.active_to}`;
      } else if (s.active_from > date) {
        status = 'pending';
        note = ` · starts ${s.active_from}`;
      }
      return { staff: s, status, note };
    });
    const rank = { active: 0, pending: 1, inactive: 2 } as const;
    list.sort((a, b) => {
      if (rank[a.status] !== rank[b.status]) return rank[a.status] - rank[b.status];
      return a.staff.display_name.localeCompare(b.staff.display_name);
    });
    return list;
  }, [staff, date]);

  const [staffId, setStaffId] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [breakMin, setBreakMin] = useState(0);
  const [overrideReason, setOverrideReason] = useState('');
  const [conflicts, setConflicts] = useState<ConflictCode[] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (shift) {
      setStaffId(shift.staff_id ?? '');
      setIsOpen(shift.is_open);
      setStartTime(shift.start_time.slice(0, 5));
      setEndTime(shift.end_time.slice(0, 5));
      setBreakMin(shift.break_minutes);
      setOverrideReason(shift.override_reason ?? '');
    } else {
      setStaffId(prefillStaffId ?? '');
      setIsOpen(false);
      setStartTime(defaultStartTime.slice(0, 5));
      setEndTime(defaultEndTime.slice(0, 5));
      setBreakMin(0);
      setOverrideReason('');
    }
    setConflicts(null);
    setError(null);
  }, [open, shift, defaultStartTime, defaultEndTime, prefillStaffId]);

  if (!open) return null;

  async function trySave(reason: string | null) {
    setError(null);
    setSubmitting(true);
    try {
      await upsertShift({
        id: shift?.id ?? null,
        location_id: locationId,
        shift_date: date,
        start_time: `${startTime}:00`,
        end_time: `${endTime}:00`,
        break_minutes: breakMin,
        staff_id: isOpen ? null : staffId || null,
        is_open: isOpen,
        override_reason: reason,
      });
      await onSaved();
      onClose();
    } catch (err) {
      const e = err as Error & { conflicts?: ConflictCode[] };
      if (e.conflicts && e.conflicts.length > 0) {
        setConflicts(e.conflicts);
      } else {
        setError(e.message);
      }
    } finally {
      setSubmitting(false);
    }
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!isOpen && !staffId) {
      setError('Pick a staff member, or check "Open shift".');
      return;
    }
    if (endTime <= startTime) {
      setError('End time must be after start time.');
      return;
    }
    void trySave(null);
  }

  function onConfirmOverride() {
    if (!overrideReason.trim()) {
      setError('A reason is required to override conflicts.');
      return;
    }
    void trySave(overrideReason.trim());
  }

  async function onCancelShift() {
    if (!shift) return;
    const reason = prompt('Reason for cancellation? (recorded in audit log)');
    if (!reason || !reason.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await cancelShift(shift.id, reason.trim());
      await onSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-lg border border-border bg-card shadow-lg">
        <header className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-base font-medium">{isEdit ? 'Edit shift' : 'Add shift'}</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Close">×</button>
        </header>

        <form onSubmit={onSubmit} className="p-4 space-y-3">
          <div className="text-xs text-muted-foreground">
            {date} · {formatTimeOfDay(`${startTime}:00`, timeFormat)}–{formatTimeOfDay(`${endTime}:00`, timeFormat)}
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isOpen} onChange={(e) => setIsOpen(e.target.checked)} className="h-4 w-4" />
            Open shift (no staff yet)
          </label>

          {!isOpen && (
            <Field label="Staff" htmlFor="staff_id">
              <select id="staff_id" value={staffId} onChange={(e) => setStaffId(e.target.value)} className={inputClass} required={!isOpen}>
                <option value="">— pick staff —</option>
                {staffOptions.map(({ staff: s, note }) => (
                  <option key={s.id} value={s.id}>
                    {s.display_name} · {s.job_type.replace('_', ' ')}{note}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <div className="grid grid-cols-3 gap-3">
            <Field label="Start" htmlFor="start_time">
              <input id="start_time" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className={inputClass} required />
            </Field>
            <Field label="End" htmlFor="end_time">
              <input id="end_time" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className={inputClass} required />
            </Field>
            <Field label="Break (min)" htmlFor="break_min">
              <input id="break_min" type="number" min={0} value={breakMin} onChange={(e) => setBreakMin(Number(e.target.value))} className={inputClass} />
            </Field>
          </div>

          {error && <p className="rounded-md border border-destructive bg-destructive/10 p-2 text-xs text-destructive">{error}</p>}

          {conflicts && (
            <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3">
              <p className="text-sm font-medium text-amber-900">Conflicts: {conflicts.join(', ')}</p>
              <p className="text-xs text-amber-800">To save anyway, give a reason (recorded in audit log).</p>
              <textarea value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder="e.g. inventory delivery cover" rows={2} className={inputClass} />
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => { setConflicts(null); setOverrideReason(''); }} className="rounded-md border border-border px-3 py-1 text-xs hover:bg-muted/40">Cancel override</button>
                <button type="button" onClick={onConfirmOverride} disabled={submitting} className="rounded-md bg-amber-600 text-white px-3 py-1 text-xs font-medium hover:bg-amber-700 disabled:opacity-50">Save with override</button>
              </div>
            </div>
          )}

          <footer className="flex items-center justify-between pt-2 border-t border-border">
            <div>
              {isEdit && (
                <button type="button" onClick={onCancelShift} disabled={submitting} className="text-xs text-destructive hover:underline">Cancel shift</button>
              )}
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted/40">Close</button>
              <button type="submit" disabled={submitting} className="rounded-md bg-primary text-primary-foreground px-4 py-1.5 text-sm font-medium hover:opacity-90 disabled:opacity-50">{submitting ? 'Saving…' : isEdit ? 'Save changes' : 'Add shift'}</button>
            </div>
          </footer>
        </form>
      </div>
    </div>
  );
}

function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label htmlFor={htmlFor} className="block text-xs font-medium">{label}</label>
      {children}
    </div>
  );
}

const inputClass = 'w-full rounded-md border border-input bg-background px-3 py-2 text-sm';
